import { useContext, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { SelectedMeeting } from "../../../pages/notes/meetingNotes";
import EditableMeetingInfo from "../EditableMeetingInfo";
import { MeetingItem } from "../../../models/meetingItem";
import { PersonalNote } from "../../../models/personalNote";
import { Context } from "../../context";
import { AppContext } from "../../../models/applicationState";
import * as itemActions from "../../../actions/itemActions";
import { ItemActions } from "../../../actions/itemActions";
import { bindActionCreators } from "../../../actions/actionCreators";
import { keyframes } from "@mui/system";
import { useTranslation } from "react-i18next";
import ConfirmationPanel from "../../confirmationPanel";
import { QuickTaskEditingPanel } from "../../quickTaskEditingPanel";
import {
  convertToRepresentation,
  getMeetingItemFromRepresentation,
  MeetingItemRepresentation,
} from "../../../models/meetingItemRepresentation";

interface ReviewStepProps {
  onNext: () => void;
  onBack: () => void;
  items: MeetingItem[];
  onItemsChange: (items: MeetingItem[]) => void;
  selectedMeeting: SelectedMeeting | null;
  onMeetingChange: (meeting: SelectedMeeting) => void;
  personalNotes: PersonalNote[];
  onError: (message: string, error?: unknown) => void;
}

const typeColors: Record<string, { bg: string; text: string }> = {
  task: { bg: "#DBEAFE", text: "#1E40AF" },
  info: { bg: "#FEF3C7", text: "#92400E" },
  decision: { bg: "#D1FAE5", text: "#065F46" },
};

const cardEnter = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, 38px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const ReviewStep = ({
  onBack,
  items,
  onItemsChange,
  selectedMeeting,
  onMeetingChange,
  personalNotes,
  onError,
}: ReviewStepProps) => {
  const { t } = useTranslation();
  const { dispatch } = useContext(Context) as AppContext;
  const actions = useMemo(
    () => bindActionCreators(itemActions, dispatch) as unknown as ItemActions,
    [dispatch],
  );

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<MeetingItem | null>(null);
  const [detailsItem, setDetailsItem] = useState<MeetingItemRepresentation | null>(null);

  const allItemRepresentations: MeetingItemRepresentation[] = useMemo(
    () =>
      items.map((item) => {
        const itemId = item.id || "";
        const normalized = { ...item, id: itemId };
        const representation = convertToRepresentation([normalized])[0] as MeetingItemRepresentation;
        return { ...representation, id: itemId, overdue: false, data: normalized } as MeetingItemRepresentation;
      }),
    [items],
  );

  const openDetails = (item: MeetingItem) => {
    const itemId = item.id || "";
    const normalized = { ...item, id: itemId };
    const representation = convertToRepresentation([normalized])[0] as MeetingItemRepresentation;
    setDetailsItem({ ...representation, id: itemId, overdue: false, data: normalized } as MeetingItemRepresentation);
  };

  const handleUpdateDetailsItem = async (updatedRepresentation: MeetingItemRepresentation) => {
    const updatedItem = getMeetingItemFromRepresentation(updatedRepresentation);
    if (!updatedItem) return;
    updatedItem.id = updatedItem.id || updatedRepresentation.id;
    if (selectedMeeting?.id && updatedItem.id) {
      try {
        const savedItem = await actions.save(selectedMeeting.id, updatedItem);
        onItemsChange(items.map((item) => (item.id === savedItem.id ? savedItem : item)));
      } catch (error) {
        onError(t("meetingNotes.review.errors.deleteItem"), error);
      }
    }
  };

  const showDeleteConfirmation = (item: MeetingItem) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const hideDeleteConfirmation = () => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const confirmDeleteItem = async () => {
    if (!itemToDelete) return;

    if (selectedMeeting?.id && itemToDelete.id) {
      try {
        await actions.remove(selectedMeeting.id, itemToDelete);
      } catch (error) {
        onError(t("meetingNotes.review.errors.deleteItem"), error);
      }
    }

    onItemsChange(items.filter((item) => item.id !== itemToDelete.id));
    hideDeleteConfirmation();
  };

  // Summary counts
  const taskCount = items.filter((item) => item.types?.includes("task")).length;
  const decisionCount = items.filter((item) =>
    item.types?.includes("decision"),
  ).length;
  const informationCount = items.filter((item) => item.types?.includes("info")).length;
  const getItemTypeLabel = (itemType: string) => {
    if (itemType === "decision") {
      return t("meetingNotes.common.itemTypes.decision");
    }

    if (itemType === "info") {
      return t("meetingNotes.common.itemTypes.info");
    }

    return t("meetingNotes.common.itemTypes.task");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 200px)",
      }}
    >
      {/* Meeting info header — editable */}
      {selectedMeeting && (
        <Box
          sx={{
            px: 3,
            py: 1,
            backgroundColor: "#F8F9FC",
            borderBottom: "1px solid #E5E7EB",
            borderRadius: "8px 8px 0 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <EditableMeetingInfo
            meeting={selectedMeeting}
            onMeetingChange={onMeetingChange}
            layout="compact"
          />
        </Box>
      )}

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ color: "#242C63", fontWeight: 600 }}>
          {t("meetingNotes.review.title")}
        </Typography>
        <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.5 }}>
          {t("meetingNotes.review.description")}
        </Typography>
      </Box>

      {/* Summary cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Card
          variant="outlined"
          sx={{
            flex: 1,
            borderColor: "#E5E7EB",
            borderWidth: "2px",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #FFFFFF 0%, #f4f4f4 100%)",
            animation: `${cardEnter} 500ms cubic-bezier(0.22, 1, 0.36, 1) 0ms both`,
          }}
        >
          <CardContent sx={{ py: 2, px: 3, "&:last-child": { pb: 2 } }}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 700,
                color: "#6B7280",
                textTransform: "uppercase",
              }}
            >
              {t("meetingNotes.review.summary.totalItems")}
            </Typography>
            <Typography
              sx={{ fontSize: "1.75rem", fontWeight: 700, color: "#242C63" }}
            >
              {items.length}
            </Typography>
          </CardContent>
        </Card>
        <Card
          variant="outlined"
          sx={{
            flex: 1,
            borderColor: "#E5E7EB",
            borderWidth: "2px",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #FFFFFF 0%, #f4f4f4 100%)",
            animation: `${cardEnter} 500ms cubic-bezier(0.22, 1, 0.36, 1) 150ms both`,
          }}
        >
          <CardContent sx={{ py: 2, px: 3, "&:last-child": { pb: 2 } }}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 700,
                color: "#6B7280",
                textTransform: "uppercase",
              }}
            >
              {t("meetingNotes.review.summary.tasks")}
            </Typography>
            <Typography
              sx={{ fontSize: "1.75rem", fontWeight: 700, color: typeColors.task.text }}
            >
              {taskCount}
            </Typography>
          </CardContent>
        </Card>
        <Card
          variant="outlined"
          sx={{
            flex: 1,
            borderColor: "#E5E7EB",
            borderWidth: "2px",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #FFFFFF 0%, #f4f4f4 100%)",
            animation: `${cardEnter} 500ms cubic-bezier(0.22, 1, 0.36, 1) 300ms both`,
          }}
        >
          <CardContent sx={{ py: 2, px: 3, "&:last-child": { pb: 2 } }}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 700,
                color: "#6B7280",
                textTransform: "uppercase",
              }}
            >
              {t("meetingNotes.review.summary.decisions")}
            </Typography>
            <Typography
              sx={{ fontSize: "1.75rem", fontWeight: 700, color: typeColors.decision.text }}
            >
              {decisionCount}
            </Typography>
          </CardContent>
        </Card>
        <Card
          variant="outlined"
          sx={{
            flex: 1,
            borderColor: "#E5E7EB",
            borderWidth: "2px",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #FFFFFF 0%, #f4f4f4 100%)",
            animation: `${cardEnter} 500ms cubic-bezier(0.22, 1, 0.36, 1) 450ms both`,
          }}
        >
          <CardContent sx={{ py: 2, px: 3, "&:last-child": { pb: 2 } }}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 700,
                color: "#6B7280",
                textTransform: "uppercase",
              }}
            >
              {t("meetingNotes.common.itemTypes.info")}
            </Typography>
            <Typography
              sx={{ fontSize: "1.75rem", fontWeight: 700, color: typeColors.info.text }}
            >
              {informationCount}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Items list - scrollable */}
      <Card
        variant="outlined"
        sx={{
          borderColor: "#E5E7EB",
          backgroundColor: "#FFFFFF",
          flex: 1,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 3, height: "100%", overflow: "auto" }}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "#64748B",
              mb: 2,
            }}
          >
            {t("meetingNotes.review.itemsToDistribute")}
          </Typography>

          {items.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4, color: "#6B7280" }}>
              <Typography>
                {t("meetingNotes.review.empty")}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {items.map((item) => {
                const itemType = item.types?.[0] || "task";
                const typeColor = typeColors[itemType] || typeColors.task;

                return (
                  <Box
                    key={item.id}
                    sx={{
                      p: 2,
                      borderRadius: "8px",
                      border: "1px solid #E5E7EB",
                      backgroundColor: "#FFFFFF",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        borderColor: "#CBD5E1",
                        boxShadow: "0 1px 3px rgba(36, 44, 99, 0.08)",
                      },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                    >
                      <Box sx={{ flex: 1 }}>
                        {item.category && (
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              fontWeight: 700,
                              color: "#64748B",
                              mb: 0.5,
                              textTransform: "uppercase",
                            }}
                          >
                            {item.category}
                          </Typography>
                        )}
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            color: "#1A2048",
                          }}
                        >
                          {item.content}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          <Chip
                            label={getItemTypeLabel(itemType)}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.6875rem",
                              fontWeight: 500,
                              backgroundColor: typeColor.bg,
                              color: typeColor.text,
                              textTransform: "capitalize",
                            }}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Tooltip title={t("meetingNotes.itemGrid.menu.viewDetails")} arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={() => openDetails(item)}
                            sx={{
                              color: "#64748B",
                              "&:hover": { color: "#242C63" },
                            }}
                          >
                            <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={() => showDeleteConfirmation(item)}
                          sx={{
                            color: "#64748B",
                            "&:hover": { color: "#EF4444" },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Footer buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-start", pt: 3 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{
            textTransform: "none",
            borderColor: "#E5E7EB",
            color: "#1A2048",
            "&:hover": { borderColor: "#C3C7CC" },
          }}
        >
          {t("button.back")}
        </Button>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmationPanel
        showModal={deleteConfirmOpen}
        hideModal={hideDeleteConfirmation}
        confirmModal={confirmDeleteItem}
        entry={itemToDelete}
        type="item"
        action="delete"
      />

      {/* Item Details Modal */}
      {detailsItem && (
        <QuickTaskEditingPanel
          meetingItem={detailsItem}
          open={Boolean(detailsItem)}
          allowMetadataEditing={true}
          allowAttachmentsEditing={true}
          showPersonalNotesButton={false}
          items={allItemRepresentations}
          onNavigateTo={(item) => setDetailsItem(item)}
          onClose={() => setDetailsItem(null)}
          onUpdate={handleUpdateDetailsItem}
        />
      )}
    </Box>
  );
};

export default ReviewStep;
